import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, tap, map, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse, ApiService } from '@app-shared/public-api';
import { Permission, Role } from '../enums';

export interface LoginRequest {
  login_id: string;
  password: string;
}

export interface LoginResponse {
  data: {
    access_token: string;
    user: {
      id: number;
      name: string;
      email: string;
      login_id: string;
    };
  };
}

interface IRole {
  id: number;
  name: Role;
  guard_name: string;
  created_at: string;
  updated_at: string;
  pivot: {
    model_type: string;
    model_id: number;
    role_id: number;
  };
}

interface PermissionType {
  id: number;
  name: Permission;
  guard_name: string;
  created_at: string;
  updated_at: string;
  pivot: {
    role_id: number;
    permission_id: number;
  };
}

export interface UserProfile {
  id: number;
  name: string;
  email: string;
  login_id: string;
  email_verified_at: string | null;
  is_active: boolean;
  is_reset_after_creating: 0 | 1;
  is_reset_password: 0 | 1;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  permissions: PermissionType[];
  roles: IRole[];
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private tokenKey = 'master_token';
  private userKey = 'master_user';
  private currentUserSubject = new BehaviorSubject<UserProfile | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(private http: HttpClient, private apiService: ApiService) {}

  login(credentials: LoginRequest): Observable<UserProfile> {
    return this.http
      .post<LoginResponse>(`${environment.apiUrl}/auth/login`, credentials)
      .pipe(
        tap((response) => {
          if (response.data?.access_token) {
            sessionStorage.setItem(this.tokenKey, response.data.access_token);
          }
        }),
        switchMap(() => {
          return this.getProfile();
        }),
        tap((response) => {
          if (response.data) {
            sessionStorage.setItem(this.userKey, JSON.stringify(response.data));
            this.currentUserSubject.next(response.data);
          }
        }),
        map((response) => response.data || null)
      );
  }

  getProfile(): Observable<{ data: UserProfile }> {
    return this.http.get<{ data: UserProfile }>(
      `${environment.apiUrl}/auth/me`
    );
  }

  logout(): Observable<any> {
    return this.http.post(`${environment.apiUrl}/auth/logout`, {}).pipe(
      tap(() => {
        sessionStorage.removeItem(this.tokenKey);
        sessionStorage.removeItem(this.userKey);
        this.currentUserSubject.next(null);
      })
    );
  }

  getToken(): string | null {
    return sessionStorage.getItem(this.tokenKey);
  }

  getStoredUser(): UserProfile | null {
    const userStr = sessionStorage.getItem(this.userKey);

    return userStr && userStr !== 'undefined' ? JSON.parse(userStr) : null;
  }

  clearUser() {
    sessionStorage.removeItem(this.tokenKey);
    sessionStorage.removeItem(this.userKey);
    this.currentUserSubject.next(null);
  }
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getCurrentUser(): UserProfile | null {
    return this.currentUserSubject.value;
  }

  loadUser(): Observable<UserProfile | null> {
    return this.getProfile().pipe(
      tap((res) => {
        if (res?.data) {
          sessionStorage.setItem(this.userKey, JSON.stringify(res.data));
          this.currentUserSubject.next(res.data);
        }
      }),
      map((res) => res.data ?? null)
    );
  }

  resetPassword(data: {
    new_password: string;
    new_password_confirmation: string;
  }): Observable<ApiResponse<null>> {
    return this.apiService.post(
      `${environment.apiUrl}/auth/password-reset`,
      data
    );
  }
  changePassword(data: {
    new_password: string;
    new_password_confirmation: string;
  }): Observable<ApiResponse<null>> {
    return this.apiService.put(
      `${environment.apiUrl}/auth/change-password`,
      data
    );
  }

  updateProfile(data: { name: string }): Observable<ApiResponse<null>> {
    return this.apiService.put(
      `${environment.apiUrl}/auth/update-profile`,
      data
    );
  }
}
